import type { ReactElement } from 'react';
import { useGamesSite } from '../context';
import { Breadcrumbs } from '../components/PublicSiteFrame';
import { GameRow } from '../components/GameRow';
import { gameCount } from '../format';
import { genreGradient, genreLook } from '../genres';
import { GAMES_HOME_PATH, gamesGenrePath } from '../routes';
import type { GameGenrePageData, GameSort } from '../types';

const SORTS: Array<{ id: GameSort; label: string }> = [
  { id: 'popular', label: 'Popular' },
  { id: 'newest', label: 'New' },
  { id: 'name', label: 'A–Z' },
];

/**
 * One genre: its banner, a sort, and its games. A snapshot passes every game;
 * the app passes the pages it has loaded and `onLoadMore` while there are more.
 */
export function GenreBody({
  data,
  sort = 'popular',
  onLoadMore,
  loadingMore = false,
}: {
  data: GameGenrePageData;
  sort?: GameSort;
  onLoadMore?: () => void;
  loadingMore?: boolean;
}): ReactElement {
  const { Link } = useGamesSite();
  const { genre, games, total, subgenres = [] } = data;
  return (
    <div className="fg-wrap">
      <Breadcrumbs
        trail={[
          { label: 'Games', href: GAMES_HOME_PATH },
          // A sub-genre sits under its parent: Games › Strategy › Tower Defense.
          ...(genre.parent
            ? [{ label: genre.parent.label, href: gamesGenrePath(genre.parent.id) }]
            : []),
          { label: genre.label },
        ]}
      />
      <header className="fg-banner" style={{ backgroundImage: genreGradient(genre.id) }}>
        <span className="fg-banner__glyph" aria-hidden="true">
          {genreLook(genre.id).glyph}
        </span>
        <h1 className="fg-banner__title">{genre.label} games</h1>
        <p className="fg-banner__count">{gameCount(total)}</p>
      </header>
      {subgenres.length > 0 && (
        <nav aria-label={`Kinds of ${genre.label.toLowerCase()} game`} className="fg-subgenres">
          <span className="fg-subgenres__label">Includes</span>
          {subgenres.map((sub) => (
            <Link key={sub.id} href={gamesGenrePath(sub.id)} className="fg-subgenres__link">
              {sub.label}
            </Link>
          ))}
        </nav>
      )}
      <nav aria-label="Sort games" className="fg-sort">
        {SORTS.map((s) => (
          <Link
            key={s.id}
            href={gamesGenrePath(genre.id, s.id)}
            className="fg-sort__option"
            aria-current={s.id === sort ? 'page' : undefined}
          >
            {s.label}
          </Link>
        ))}
      </nav>
      {games.length === 0 ? (
        <div className="fg-empty">
          <p className="fg-empty__title">No {genre.label.toLowerCase()} games yet.</p>
          <p>
            <Link href={GAMES_HOME_PATH}>See every game</Link>
          </p>
        </div>
      ) : (
        <ul className="fg-list">
          {games.map((game) => (
            <li key={game.slug}>
              <GameRow game={game} iconSize="md" headingLevel={2} />
            </li>
          ))}
        </ul>
      )}
      {onLoadMore && (
        <div className="fg-more">
          <button
            type="button"
            className="fg-more__button"
            onClick={onLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading more games…' : 'Show more games'}
          </button>
        </div>
      )}
    </div>
  );
}
